import {
  Badge,
  Button,
  LoadingSpinner,
  ShareDialog,
  SlideoutMenu
} from '@goodneighbor/ui';
import {
  ArrowLeft,
  Send01,
  Share07,
  UsersPlus
} from '@goodneighbor/ui/icons/untitled';
import styles from './eventSlideOut.module.scss';
import { type Event } from '@goodneighbor/api/schemas/events/event.schema';
import ExpandableText from '@/components/ExpandableText';
import Link from 'next/link';
import useEventQuery from '@/hooks/api/events/useEventQuery';
import { useMemo, useState } from 'react';
import { isErrorResponse } from '@/services/typeguards';
import RegistrationModal from '@/components/modals/RegistrationModal';
import ExternalRegistrationModal from '@/components/modals/ExternalRegistrationModal/ExternalRegistrationModal';
import { InviteAFriendModal } from '@/components/modals/InviteAFriendModal';
import { PartnerPreviewCard } from '@/components/PartnerPreviewCard';
import { activityTypeMap, causeTypeMap, skillTypeMap } from '@/services/event';
import { getEventMetaSections } from './services/eventMetaSection';
import {
  getRegisterButtonLabel,
  isRegisterButtonDisabled,
  isExternalEvent,
  getIsEventFull,
  getIsEventPast
} from './services/event';
import useEventRegistrationCountQuery from '@/hooks/api/events/useEventRegistrationCountQuery';
import Sidebar from './components/Sidebar';
import RegisterAndProgress from './components/RegisterAndProgress';
import RegistrationCounter from './components/RegistrationCounter';
import { AdvancedMarker, Map } from '@vis.gl/react-google-maps';
import EventMarker from '@/app/events/components/EventMarker';

type EventSlideOutProps = {
  event?: Event | null;
  eventId?: string | null;
  showRegistrationCounter?: boolean;
} & Omit<React.ComponentProps<typeof SlideoutMenu>, 'children'>;

const backToMapLabel = 'Back to map';

export default function EventSlideOut({
  event: originalEvent,
  eventId,
  showRegistrationCounter = true,
  ...props
}: EventSlideOutProps) {
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const { isLoading, data, error } = useEventQuery({
    id: eventId || originalEvent?.id || ''
  });

  const event = useMemo(() => {
    if (data && !isErrorResponse(data) && data.data) {
      return data.data;
    }
    return null;
  }, [data]);

  const {
    data: registrationCountData,
    isLoading: isRegistrationCountLoading,
    error: registrationCountError
  } = useEventRegistrationCountQuery({
    enabled: showRegistrationCounter && !!event?.id,
    id: event?.id || ''
  });

  const isPageLoading = isLoading || isRegistrationCountLoading;

  const registrationCount = useMemo(() => {
    if (
      registrationCountData &&
      !isErrorResponse(registrationCountData) &&
      registrationCountData.data
    ) {
      return registrationCountData.data.registrationCount;
    }
    return 0;
  }, [registrationCountData]);

  const isEventFull = useMemo(
    () => getIsEventFull(event, registrationCount),
    [event, registrationCount]
  );

  const isAlreadyRegistered = event?.registered ?? false;
  const isEventPast = getIsEventPast(event);

  const registerButtonState = { isAlreadyRegistered, isEventFull, isEventPast };

  const eventShareUrl = useMemo(() => {
    const currentEventId = event?.id || eventId || originalEvent?.id;
    if (!currentEventId) return '';

    const origin =
      typeof window === 'undefined'
        ? process.env.NEXT_PUBLIC_VOLUNTEER_URL
        : window.location.origin;

    if (!origin) return '';

    const url = new URL('/events', origin);
    url.searchParams.set('eventId', currentEventId);
    return url.toString();
  }, [event?.id, eventId, originalEvent?.id]);

  return (
    <SlideoutMenu
      {...props}
      overlayClassName={styles.slideOutOverlay}
      className={styles.slideOutMenu}
      dialogClassName={styles.slideOutDialog}
      dialogProps={{
        'aria-label': 'Events'
      }}
    >
      {({ close }) => (
        <>
          {isPageLoading && (
            <div className={styles.loadingContainer}>
              <LoadingSpinner />
            </div>
          )}
          {(error || registrationCountError) && !isPageLoading && (
            <div className={styles.error}>Error loading event data.</div>
          )}
          {event &&
            (isExternalEvent(event) ? (
              <ExternalRegistrationModal
                isOpen={registrationOpen}
                onOpenChange={setRegistrationOpen}
                event={event}
                organizationName={event.organization?.name || ''}
              />
            ) : (
              <RegistrationModal
                isOpen={registrationOpen}
                onOpenChange={setRegistrationOpen}
                event={event}
                organizationName={event.organization?.name || ''}
              />
            ))}
          {event && (
            <InviteAFriendModal
              isOpen={inviteModalOpen}
              onOpenChange={setInviteModalOpen}
              eventId={event.id}
            />
          )}
          {event && (
            <ShareDialog
              useReactAria
              isOpen={shareDialogOpen}
              onOpenChange={setShareDialogOpen}
              shareUrl={eventShareUrl}
              title="Share Event"
              description="Encourage your friends to join you in volunteering."
            />
          )}
          {!isPageLoading && !error && event && (
            <>
              <SlideoutMenu.Header className={styles.header} hideDefaultClose>
                <Button
                  className={styles.backLink}
                  iconLeading={() => (
                    <ArrowLeft className={styles.backLinkArrow} />
                  )}
                  color="link-gray"
                  onClick={close}
                  aria-label={backToMapLabel}
                >
                  <span className={styles.backLinkText}>{backToMapLabel}</span>
                </Button>
                <div className={styles.actions}>
                  <Button
                    className={styles.actionButton}
                    color="secondary"
                    size="md"
                    iconLeading={() => <Send01 width={20} height={20} />}
                    onClick={() => setInviteModalOpen(true)}
                    aria-label="Invite a friend"
                  />
                  <Button
                    className={styles.actionButton}
                    color="secondary"
                    size="md"
                    iconLeading={() => <Share07 width={20} height={20} />}
                    aria-label="Share event"
                    onClick={() => setShareDialogOpen(true)}
                  />
                </div>
              </SlideoutMenu.Header>
              <SlideoutMenu.Content className={styles.content}>
                {event?.promoImageUrl && (
                  <img
                    className={styles.promoImage}
                    src={event.promoImageUrl}
                    alt={`${event?.title} promotional image`}
                  />
                )}
                <div className={styles.contentContainer}>
                  <div className={styles.infoSection}>
                    <div className={styles.eventInfo}>
                      <div>
                        <h1 className={styles.title}>{event?.title}</h1>
                        <h2 className={styles.host}>
                          {event?.organization?.name}
                        </h2>
                        {showRegistrationCounter && (
                          <div className={styles.mobileTitleProgress}>
                            {isExternalEvent(event) ? (
                              <div className={styles.volunteersRequested}>
                                <UsersPlus
                                  width={16}
                                  height={16}
                                  className={styles.volunteersRequestedIcon}
                                />
                                <span>{`${event?.publicVolunteersCount ?? 0} volunteers requested`}</span>
                              </div>
                            ) : (
                              <RegistrationCounter
                                className={styles.registrationCounter}
                                eventId={event.id}
                                total={event.overallCapacity || 0}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      <div className={styles.tabletRegisterActions}>
                        <RegisterAndProgress
                          event={event}
                          onRegister={() => setRegistrationOpen(true)}
                          showRegistrationCounter={showRegistrationCounter}
                        />
                      </div>
                    </div>
                    <hr className={styles.divider} />
                    <div className={styles.mobileEventMeta}>
                      {getEventMetaSections(event).map(({ title, content }) => (
                        <div key={title} className={styles.mobileEventMetaItem}>
                          <h3 className={styles.mobileEventMetaTitle}>
                            {title}
                          </h3>
                          <div className={styles.mobileEventMetaContent}>
                            {content}
                          </div>
                        </div>
                      ))}
                    </div>
                    <hr className={styles.divider} />
                    <div className={styles.section}>
                      <h3 className={styles.sectionHeading}>
                        Event Description
                      </h3>
                      <ExpandableText>
                        {event?.description || 'No description available.'}
                      </ExpandableText>
                    </div>
                    <hr className={styles.divider} />
                    <div className={styles.badgesContainer}>
                      {!!event?.causeType && (
                        <div className={styles.badgeSection}>
                          <h5 className={styles.badgesHeading}>Cause type</h5>
                          <div className={styles.badges}>
                            <Badge type="pill-color" color="blue">
                              {causeTypeMap[event?.causeType] ||
                                event?.causeType}
                            </Badge>
                          </div>
                        </div>
                      )}
                      {!!event.activityTypes.length && (
                        <div className={styles.badgeSection}>
                          <h5 className={styles.badgesHeading}>
                            Activity type
                          </h5>
                          <div className={styles.badges}>
                            {event.activityTypes.map((activityType) => (
                              <Badge
                                key={activityType.id}
                                type="pill-color"
                                color="success"
                              >
                                {activityTypeMap[activityType.name]}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {!!event.skillTypes.length && (
                        <div className={styles.badgeSection}>
                          <h5 className={styles.badgesHeading}>Skill type</h5>
                          <div className={styles.badges}>
                            {event.skillTypes.map((skillType) => (
                              <Badge
                                key={skillType.id}
                                type="pill-color"
                                color="purple"
                              >
                                {skillTypeMap[skillType.name]}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <hr className={styles.divider} />
                    <div className={styles.mobileLocation}>
                      <h3 className={styles.mobileLocationTitle}>Location</h3>
                      <div className={styles.mobileMapContainer}>
                        <Map
                          defaultCenter={{
                            lat:
                              (originalEvent || event)?.coords
                                ?.coordinates[1] || 0,
                            lng:
                              (originalEvent || event)?.coords
                                ?.coordinates[0] || 0
                          }}
                          defaultZoom={10}
                          mapId="EVENT_SLIDEOUT_MOBILE_MAP"
                          gestureHandling="none"
                        >
                          <AdvancedMarker
                            position={{
                              lat:
                                (originalEvent || event)?.coords
                                  ?.coordinates[1] || 0,
                              lng:
                                (originalEvent || event)?.coords
                                  ?.coordinates[0] || 0
                            }}
                            style={{ position: 'relative' }}
                          >
                            <EventMarker />
                          </AdvancedMarker>
                        </Map>
                      </div>
                      <p className={styles.mobileLocationCity}>
                        {event?.city}, {event?.region}
                      </p>
                    </div>
                    <hr className={styles.divider} />
                    {event && event.organization && (
                      <div className={styles.section}>
                        <h3 className={styles.sectionHeadingHost}>
                          Host Partner
                        </h3>
                        {isExternalEvent(event) ? (
                          event.organization.website ? (
                            <a
                              href={event.organization.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.partnerLink}
                            >
                              <PartnerPreviewCard
                                name={event.organization.name}
                                description={event.organization.description}
                                imageUrl={event.organization.logoImageUrl}
                              />
                            </a>
                          ) : (
                            <PartnerPreviewCard
                              name={event.organization.name}
                              description={event.organization.description}
                              imageUrl={event.organization.logoImageUrl}
                            />
                          )
                        ) : event.organization.slug ? (
                          <Link
                            href={`/organization/${event.organization.slug}`}
                          >
                            <PartnerPreviewCard
                              name={event.organization.name}
                              description={event.organization.description}
                              imageUrl={event.organization.logoImageUrl}
                            />
                          </Link>
                        ) : (
                          <PartnerPreviewCard
                            name={event.organization.name}
                            description={event.organization.description}
                            imageUrl={event.organization.logoImageUrl}
                          />
                        )}
                      </div>
                    )}
                    {!!event?.sponsors?.length && (
                      <div className={styles.section}>
                        <h3 className={styles.sectionHeadingSponsors}>
                          Event Partners
                        </h3>
                        <div className={styles.sectionSponsors}>
                          {event.sponsors.map((sponsor) => (
                            <Link
                              key={sponsor.id}
                              href={`/organization/${sponsor.slug}`}
                              className={styles.sponsorLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <PartnerPreviewCard
                                name={sponsor.name}
                                imageUrl={sponsor.logoImageUrl}
                              />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.sidebar}>
                    <Sidebar
                      event={event}
                      originalEvent={originalEvent}
                      showRegistrationCounter={showRegistrationCounter}
                      onRegister={() => setRegistrationOpen(true)}
                    />
                  </div>
                </div>
              </SlideoutMenu.Content>
              <SlideoutMenu.Footer className={styles.mobileFooter}>
                <Button
                  className={styles.mobileFooterButton}
                  size="xl"
                  color="primary"
                  onClick={() => setRegistrationOpen(true)}
                  isDisabled={isRegisterButtonDisabled(registerButtonState)}
                >
                  {getRegisterButtonLabel(registerButtonState)}
                </Button>
              </SlideoutMenu.Footer>
            </>
          )}
        </>
      )}
    </SlideoutMenu>
  );
}
